import { useMemo, useState } from 'react';

type Status = 'details' | 'compose' | 'busy' | 'received';
type Verification = {ok:true;verdict:'verified'|'tampered'|'error';code:string;sender:{firstName:string;lastName:string;email:string;country:string;status:string;pictureDataUrl?:string;transactionCode?:string;sentAt?:string};contentComparison?:{rawTextChanged:boolean;canonicalBodyEquivalent:boolean}};
type Created = {ok:true;from:string;to:string;subject:string;body:string;stampDataUrl:string;status:string};
type DrawerContext = 'verify' | 'tamper-text' | 'tamper-stamp';
type DrawerState = { open:boolean; context:DrawerContext; verification:Verification };

const API_BASE = '/api/playground/stage1';
// Bundled red X asset used by the verification-failure state.
const RED_X_URL = 'https://storage.helloreaddy.io/project_files/026e1453-3796-4207-bff6-367c8dc212ea/15026c26-c629-4e73-825e-cf84d72727d6_compressed_redX.webp';

async function submit<T>(route: string, body: object): Promise<T> {
  const response = await fetch(`${API_BASE}/${route}`, {
    method: 'POST', credentials: 'same-origin', headers: {'Content-Type':'application/json'},
    body: JSON.stringify(body)
  });
  const data = await response.json();
  if (!response.ok || data.ok === false) throw new Error(data.error || `HTTP ${response.status}`);
  return data as T;
}

function formatDateTime(value: string | null) {
  if (!value) return 'Not supplied';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not supplied';
  return date.toLocaleString('en-AU', {
    day:'numeric', month:'numeric', year:'numeric', hour:'numeric', minute:'2-digit', second:'2-digit', hour12:true
  });
}

function PlaceholderAvatar() {
  return (
    <svg viewBox="0 0 120 120" className="h-full w-full" aria-hidden="true">
      <circle cx="60" cy="60" r="58" fill="#d8d8dc" stroke="#c7c7cc" strokeWidth="2" />
      <circle cx="60" cy="42" r="18" fill="#f1f1f2" />
      <path d="M28 88c7-16 23-24 32-24s25 8 32 24" fill="#f1f1f2" />
    </svg>
  );
}

function ResultDrawer({
  drawer,
  created,
  sentAt,
  onClose,
}: {
  drawer: DrawerState | null;
  created: Created | null;
  sentAt: string | null;
  onClose: () => void;
}) {
  const verification = drawer?.verification;
  if (!drawer || !verification || !created) return null;

const verified = verification.verdict === 'verified';
const canonicalDifference = drawer.context === 'tamper-text' &&
  verification.contentComparison?.rawTextChanged === true &&
  verification.contentComparison?.canonicalBodyEquivalent === true && verified;
const unexpectedPass = drawer.context !== 'verify' && verified && !canonicalDifference;
const cleanPass = verified && !canonicalDifference && !unexpectedPass;
const isFailure = verification.verdict === 'tampered' || verification.verdict === 'error';
  const senderName = [verification.sender.firstName, verification.sender.lastName].filter(Boolean).join(' ') || 'Not supplied';
  const senderOrigin = verification.sender.country || 'Not supplied';
  const senderEmail = verification.sender.email || created.from || 'Not supplied';
  const technicalCode = verification.code || 'Not supplied';
  // The general-mail verifier retains a legacy invoice-oriented error code.
  // Translate that specific server result ONLY for the stamp-substitution demo;
  // do not modify or misrepresent the actual verifier response.
  const stampBindingMismatch = drawer.context === 'tamper-stamp' &&
    verification.verdict === 'tampered' &&
    technicalCode === 'TAMPERED:INVOICE_HASH_MISMATCH';

  const introTitle = canonicalDifference
    ? 'Formatting Difference Detected'
    : unexpectedPass ? 'Verification Result Needs Review'
    : verified
    ? 'Verified Sender Details'
    : drawer.context === 'tamper-stamp'
      ? 'bioStamp Substitution Detected'
      : drawer.context === 'tamper-text'
        ? 'Message Tampering Detected'
        : 'Verification Failed';

  const badgeText = canonicalDifference ? 'Canonical V2 check passed' : unexpectedPass ? 'Review required' : verified ? 'Verified identity' : verification.verdict==='tampered' ? 'Tampering detected' : 'Verification inconclusive';
  const ringClass = canonicalDifference || unexpectedPass ? 'border-amber-500' : verified ? 'border-green-500' : 'border-red-500';
  const badgeClass = canonicalDifference || unexpectedPass ? 'bg-amber-100 text-amber-800' : verified ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
  const titleClass = canonicalDifference || unexpectedPass ? 'text-amber-800' : verified ? 'text-green-700' : 'text-red-700';

const explanation = canonicalDifference
  ? 'The text was changed, but V2 canonicalisation treats both versions as equivalent. The live verifier accepted the canonical content hash; this does NOT mean the exact characters are unchanged.'
  : unexpectedPass
    ? 'The live verifier accepted this modified test. Review the signed-content policy and server result before treating this as secure.'
    : verified
  ? 'The sender identity and canonical message integrity were confirmed by the live V2 verification service.'
    : drawer.context === 'tamper-stamp' && technicalCode === 'TAMPERED:INVOICE_HASH_MISMATCH'
      ? 'This is a genuine registered bioStamp, but it belongs to a different message. Its signed registration details do not match this email.'
      : drawer.context === 'tamper-text' && technicalCode === 'TAMPERED:V2_MESSAGE_PAYLOAD_MISMATCH'
        ? 'The received message content no longer matches the content protected by the original registered bioStamp.'
        : 'The live V2 verification service detected that this email or bioStamp no longer matches its protected registration details.';

  const fields = [
    { label:'Sender', value: senderName },
    { label:'Sender Origin', value: senderOrigin },
    { label:'Sender Email', value: senderEmail },
    { label:'Date Sent', value: formatDateTime(verification.sender.sentAt || sentAt) },
    { label:'Your Email', value: created.to || 'Not supplied' },
    { label:'Transaction Code', value: verification.sender.transactionCode || 'Not supplied' },
    { label:'Expiry Date', value: 'Not supplied' },
  ];

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 w-full max-w-[560px] bg-white shadow-2xl border-l border-gray-200">
        <div className="flex h-full flex-col overflow-hidden">
          <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 md:px-6">
            <h3 className="text-2xl font-semibold text-gray-900">{introTitle}</h3>
            <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Close</button>
          </div>
          <div className="flex-1 overflow-y-auto bg-white px-4 py-5 md:px-6 md:py-6">
            <div className="mx-auto max-w-[470px] rounded-[28px] border border-gray-300 bg-[#f7f7f8] shadow-sm">
              <div className="rounded-t-[28px] bg-[#4b8be8] px-6 py-5 text-center text-white">
                <div className="flex items-center justify-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-2xl font-bold">
                    {cleanPass ? '✓' : isFailure ? '!' : 'i'}
                  </div>
                  <span className="text-3xl font-semibold">{cleanPass ? 'Verified Sender Details' : 'Verification Result'}</span>
                </div>
              </div>

              <div className="px-6 pb-8 pt-5">
                <div className={`mx-auto mb-5 flex h-52 w-52 items-center justify-center rounded-full border-4 bg-white shadow-sm ${ringClass}`}>
                  <div className={`flex h-[196px] w-[196px] items-center justify-center overflow-hidden rounded-full border-8 border-white bg-[#efeff1]`}>
                    {isFailure ? (
                      <img src={RED_X_URL} alt="Verification failed" className="h-28 w-28 object-contain" />
                    ) : verification.sender.pictureDataUrl ? (
                      <img src={verification.sender.pictureDataUrl} alt="Registered sender" className="h-full w-full object-cover" />
                    ) : <PlaceholderAvatar />}
                  </div>
                </div>

                <div className="mb-8 flex justify-center">
                  <div className={`inline-flex items-center gap-3 rounded-full px-6 py-3 text-2xl font-semibold ${badgeClass}`}>
                    <span className="text-3xl leading-none">{cleanPass ? '✓' : isFailure ? '✕' : 'i'}</span>
                    <span>{badgeText}</span>
                  </div>
                </div>

                <p className={`mb-2 text-center text-2xl font-semibold ${titleClass}`}>{canonicalDifference ? 'Formatting difference accepted by V2' : unexpectedPass ? 'Modification accepted — investigate' : verified ? 'Sender verified' : verification.verdict==='tampered' ? 'Tampering detected' : 'Verification inconclusive'}</p>
                <p className="mb-8 text-center text-sm leading-6 text-gray-600">{explanation}</p>

                <div className="space-y-5">
                  {fields.map((field) => (
                    <div key={field.label} className="border-b border-gray-300 pb-4">
                      <div className="mb-2 text-[15px] font-semibold text-[#3d7fe0]">{field.label}</div>
                      <div className="break-words text-[17px] leading-7 text-gray-900">{field.value}</div>
                    </div>
                  ))}
                </div>

                {stampBindingMismatch ? (
                  <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-4" role="status">
                    <div className="mb-1 text-sm font-semibold text-red-800">bioStamp message mismatch</div>
                    <p className="text-sm leading-6 text-gray-800">
                      A genuine bioStamp was substituted from another registered message. It does not
                      match the signed registration for this email, so verification failed.
                    </p>
                    <details className="mt-4 border-t border-red-200 pt-3">
                      <summary className="cursor-pointer text-sm font-medium text-gray-700">
                        Technical details (original server result)
                      </summary>
                      <p className="mt-2 break-words font-mono text-xs text-gray-700">{technicalCode}</p>
                      <p className="mt-2 text-xs leading-5 text-gray-600">
                        The server still returns its existing V2 error code. Only its explanation in the Playground has been changed.
                      </p>
                    </details>
                  </div>
                ) : (
                  <div className="mt-6 rounded-2xl border border-gray-200 bg-white px-4 py-4">
                    <div className="mb-1 text-sm font-semibold text-gray-700">Technical server result (actual V2 verdict)</div>
                    <div className="break-words text-base text-gray-900">{technicalCode}</div>
                  </div>
                )}

                <div className="mt-6 flex justify-center">
                  <button type="button" onClick={onClose} className="rounded-2xl bg-[#3173df] px-10 py-3 text-lg font-semibold text-white shadow-sm hover:opacity-95">Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default function LiveVerificationStage() {
  const [firstName,setFirstName] = useState('');
  const [lastName,setLastName] = useState('');
  const [email,setEmail] = useState('');
  const [session,setSession] = useState('');
  const [status,setStatus] = useState<Status>('details');
  const [body,setBody] = useState('Hello, this is my bioVerifier test message.');
  const [created,setCreated] = useState<Created|null>(null);
  const [error,setError] = useState('');
  const [showInbox,setShowInbox] = useState(false);
  const [verification,setVerification] = useState<Verification|null>(null);
  const [verifying,setVerifying] = useState(false);
  const [altering,setAltering] = useState(false);
  const [receivedBody,setReceivedBody] = useState('');
  const [tamperVerification,setTamperVerification] = useState<Verification|null>(null);
  const [testingTamper,setTestingTamper] = useState(false);
  const [baselineVerified,setBaselineVerified] = useState(false);
  const [stampAltered,setStampAltered] = useState(false);
  const [substituteStamp,setSubstituteStamp] = useState('');
  const [preparingStamp,setPreparingStamp] = useState(false);
  const [drawer,setDrawer] = useState<DrawerState | null>(null);
  const [sentAt,setSentAt] = useState<string | null>(null);

  async function prepare() {
    setError('');
    setStatus('busy');
    try {
      const result = await submit<{ok:true;sessionId:string}>('session',{firstName,lastName,email});
      setSession(result.sessionId);
      setStatus('compose');
    } catch(err) {
      setError(String((err as Error).message));
      setStatus('details');
    }
  }

  async function send() {
    setError('');
    setStatus('busy');
    try {
      const result = await submit<Created>('send',{sessionId:session,body});
      setCreated(result);
      setSentAt(new Date().toISOString());
      setReceivedBody(result.body);
      setAltering(false);
      setTamperVerification(null);
      setBaselineVerified(false);
      setVerification(null);
      setStampAltered(false);
      setSubstituteStamp('');
      setDrawer(null);
      setShowInbox(true);
      setStatus('received');
    } catch(err) {
      setError(String((err as Error).message));
      setStatus('compose');
    }
  }

  async function verifySender() {
    setError('');
    setVerification(null);
    setVerifying(true);
    try {
      const result = await submit<Verification>('verify',{sessionId:session});
      setVerification(result);
      setBaselineVerified(result.verdict==='verified');
      setDrawer({ open:true, context:'verify', verification: result });
    } catch(err) {
      setError(String((err as Error).message));
    } finally {
      setVerifying(false);
    }
  }

  function beginAlteration() {
    if (!created || !baselineVerified || stampAltered) return;
    setVerification(null);
    setTamperVerification(null);
    setDrawer(null);
    setError('');
    setReceivedBody(created.body);
    setAltering(true);
  }

  async function verifyAlteredMessage() {
    if (!created || !baselineVerified || !altering || receivedBody===created.body) return;
    setError('');
    setTamperVerification(null);
    setTestingTamper(true);
    try {
      const result = await submit<Verification>('alter',{sessionId:session,body:receivedBody});
      setTamperVerification(result);
      setDrawer({ open:true, context:'tamper-text', verification: result });
    } catch(err) {
      setError(String((err as Error).message));
    } finally {
      setTestingTamper(false);
    }
  }

  async function beginStampAlteration() {
    if (!created || !baselineVerified || altering || testingTamper) return;
    setError('');
    setTamperVerification(null);
    setVerification(null);
    setDrawer(null);
    setPreparingStamp(true);
    try {
      const result = await submit<{ok:true;stampDataUrl:string}>('alter',{sessionId:session,mode:'stamp',action:'prepare'});
      setSubstituteStamp(result.stampDataUrl);
      setStampAltered(true);
    } catch(err) {
      setError(String((err as Error).message));
    } finally {
      setPreparingStamp(false);
    }
  }

  async function verifySubstitutedStamp() {
    if (!stampAltered || !baselineVerified) return;
    setError('');
    setTamperVerification(null);
    setTestingTamper(true);
    try {
      const result = await submit<Verification>('alter',{sessionId:session,mode:'stamp',action:'verify'});
      setTamperVerification(result);
      setDrawer({ open:true, context:'tamper-stamp', verification: result });
    } catch(err) {
      setError(String((err as Error).message));
    } finally {
      setTestingTamper(false);
    }
  }

  function restoreStamp() {
    setStampAltered(false);
    setTamperVerification(null);
    setVerification(null);
    setDrawer(null);
    setError('');
  }

  function resetAlteration() {
    if (!created) return;
    setReceivedBody(created.body);
    setAltering(false);
    setTamperVerification(null);
    setVerification(null);
    setDrawer(null);
    setError('');
  }

  const field='w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:border-blue-600 focus:outline-none';
  const button='rounded-full bg-blue-700 px-6 py-3 text-white font-semibold disabled:opacity-50';

  const inlineStatus = useMemo(() => {
    if (altering) return 'Message text edited locally; the original bioStamp and V2 record have not changed.';
    if (stampAltered) return 'The displayed bioStamp belongs to a DIFFERENT registered message; the original message and signed fields remain unchanged.';
    if (baselineVerified) return 'Original message passed genuine V2 verification.';
    return 'V2 stamp registered; recipient-side verification has not yet been performed.';
  }, [altering, stampAltered, baselineVerified]);

  return (
    <section id="real-verification-playground" data-biostamp-build="stage1-7.5-live-drawer" className="w-full bg-white px-4 py-14 md:px-8 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <p className="mb-2 text-sm font-semibold uppercase tracking-widest text-blue-700">Stage 1 · Live V2 generation and verification</p>
        <h2 className="mb-3 text-3xl font-bold text-gray-900 md:text-4xl">Create your own bioStamp demonstration</h2>
        <p className="mb-8 text-gray-600">Prepare a virtual message and generate a real server-registered V2 bioStamp. No extension is required for this step. Verify Sender uses the real V2 verification service. After verifying the original, change the received text or replace its stamp with a different genuinely registered bioStamp to see the real V2 verifier&apos;s response.</p>
        <div className="space-y-5 rounded-2xl border border-gray-200 p-5 shadow-sm md:p-8">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="block text-sm font-medium text-gray-700">First name<input className={`${field} mt-1`} maxLength={80} disabled={status!=='details'} value={firstName} onChange={e=>setFirstName(e.target.value)}/></label>
            <label className="block text-sm font-medium text-gray-700">Last name<input className={`${field} mt-1`} maxLength={80} disabled={status!=='details'} value={lastName} onChange={e=>setLastName(e.target.value)}/></label>
          </div>
          <label className="block text-sm font-medium text-gray-700">Your email address<input type="email" className={`${field} mt-1`} maxLength={254} disabled={status!=='details'} value={email} onChange={e=>setEmail(e.target.value)}/></label>
          <p className="text-xs text-gray-500">Contact details are used only for this short-lived demonstration session. Marketing signup will be added separately with explicit consent.</p>
          {status==='details' && <button className={button} type="button" disabled={!firstName.trim()||!lastName.trim()||!email.includes('@')} onClick={prepare}>Create bioStamp</button>}
          {(status==='compose'||status==='received'||status==='busy') && <div className="space-y-4 border-t pt-5">
            <div className="flex items-center gap-3"><div className="flex h-20 w-20 items-center justify-center rounded-xl border-2 border-dashed border-blue-400 bg-blue-50 p-2 text-center text-xs text-blue-700">Provisional stamp</div><div><strong>Compose your message</strong><p className="text-sm text-gray-600">The final stamp will only be registered when you select Send.</p></div></div>
            <label className="block text-sm font-medium text-gray-700">Message body<textarea rows={5} className={`${field} mt-1`} maxLength={1000} disabled={status!=='compose'} value={body} onChange={e=>setBody(e.target.value)}/></label>
            {status==='compose' && <button className={button} type="button" disabled={!body.trim()} onClick={send}>Send to virtual inbox</button>}
            {status==='busy' && <p role="status" className="text-blue-700">Processing your secure request…</p>}
          </div>}
          {error && <p role="alert" className="text-red-700">{error}</p>}
        </div>

        {showInbox && created && <div className="mt-6 overflow-hidden rounded-2xl border border-blue-200 shadow-sm">
          <div className="bg-blue-50 px-6 py-4 font-semibold text-blue-900">Virtual inbox — received message</div>
          <div className="space-y-4 p-6">
            <div className="text-sm">
              <p><b>From:</b> {created.from}</p>
              <p><b>To:</b> {created.to}</p>
              <p><b>Subject:</b> {created.subject}</p>
            </div>

            {altering ? <div className="space-y-2 border-t pt-4">
              <label className="block text-sm font-semibold text-gray-800" htmlFor="playground-altered-body">Edit the received message (original bioStamp remains attached)</label>
              <textarea id="playground-altered-body" rows={5} maxLength={1000} className={field} value={receivedBody} disabled={testingTamper} onChange={e=>{setReceivedBody(e.target.value);setTamperVerification(null);}} />
              <p className="text-xs text-gray-600">The original message and signed V2 record remain unchanged on the server.</p>
            </div> : <div className="whitespace-pre-wrap break-words border-t pt-4">{created.body}</div>}

            <img className="h-auto w-40" alt={stampAltered ? 'Substituted V2 bioStamp from a different registered email' : 'Genuine V2 registered bioStamp'} src={stampAltered ? substituteStamp : created.stampDataUrl} />
            <p role="status" className="text-sm text-amber-800">{inlineStatus}</p>

            <div className="flex flex-wrap gap-3">
              <button className={button} type="button" disabled={verifying || testingTamper || altering || stampAltered || preparingStamp} onClick={verifySender}>{verifying ? 'Verifying with V2 server…' : 'Verify Sender'}</button>
              <button className={button} type="button" disabled={!baselineVerified || altering || stampAltered || preparingStamp || testingTamper} onClick={beginAlteration}>Alter Message Text</button>
              <button className={button} type="button" disabled={!baselineVerified || altering || stampAltered || preparingStamp || testingTamper} onClick={beginStampAlteration}>{preparingStamp ? 'Registering substitute…' : 'Alter bioStamp'}</button>
              <button className={button} type="button" disabled={testingTamper || preparingStamp || (!stampAltered && (!altering || !receivedBody.trim() || receivedBody===created.body))} onClick={stampAltered ? verifySubstitutedStamp : verifyAlteredMessage}>{testingTamper ? 'Checking V2 integrity…' : 'Send Manipulated Message'}</button>
              {stampAltered && <button type="button" className="rounded-full border border-gray-400 px-6 py-3 font-semibold text-gray-800" disabled={testingTamper} onClick={restoreStamp}>Restore original bioStamp</button>}
              {altering && <button type="button" className="rounded-full border border-gray-400 px-6 py-3 font-semibold text-gray-800" disabled={testingTamper} onClick={resetAlteration}>Restore original</button>}
            </div>
          </div>
        </div>}
      </div>

      <ResultDrawer
        drawer={drawer}
        created={created}
        sentAt={sentAt}
        onClose={() => setDrawer(null)}
      />
    </section>
  );
}
